import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import FrontHeader from '../components/common/FrontHeader';
import FrontFooter from '../components/common/FrontFooter';
import './ServiceProviders.css';

const API_BASE = (
  process.env.REACT_APP_API_URL || 'http://localhost:5000/api'
).replace(/\/+$/, '');

const cleanText = value =>
  typeof value === 'string' ? value.trim() : '';

const cleanList = value =>
  Array.isArray(value)
    ? [...new Set(value.map(cleanText).filter(Boolean))]
    : [];

const numberValue = value => {
  const number = Number(value);
  return Number.isFinite(number) && number >= 0 ? number : 0;
};

const getCompanyName = provider =>
  cleanText(provider.company) ||
  cleanText(provider.name) ||
  'Service provider';

const getDescription = provider =>
  cleanText(provider.description) || cleanText(provider.bio);

const getWebsite = value => {
  const input = cleanText(value);

  if (!input) return '';

  try {
    const url = new URL(
      /^[a-z][a-z0-9+.-]*:/i.test(input)
        ? input
        : `https://${input}`
    );

    return ['http:', 'https:'].includes(url.protocol) ? url.href : '';
  } catch {
    return '';
  }
};

const resolveAvatar = provider => {
  const raw = cleanText(provider?.avatar);

  if (
    raw &&
    /^data:image\/(?:png|jpeg|jpg|webp|gif);base64,/i.test(raw)
  ) {
    return raw;
  }

  if (raw && /^https?:\/\//i.test(raw)) {
    return raw;
  }

  if (provider?._id) {
    return `${API_BASE}/avatar/${provider._id}`;
  }

  return '';
};

const getMemberSince = value => {
  if (!value) return 'Not provided';

  const date = new Date(value);

  return Number.isNaN(date.getTime())
    ? 'Not provided'
    : date.toLocaleDateString(undefined, {
        month: 'short',
        year: 'numeric'
      });
};

function ExpandableDescription({ value }) {
  const [expanded, setExpanded] = useState(false);
  const content = cleanText(value);
  const limit = 600;
  const canExpand = content.length > limit;

  if (!content) {
    return (
      <p className="spd-muted">
        This company has not added its description yet.
      </p>
    );
  }

  return (
    <div className="spd-description">
      <p>
        {canExpand && !expanded
          ? `${content.slice(0, limit).trim()}…`
          : content}
      </p>

      {canExpand && (
        <button
          type="button"
          className="spd-text-button"
          aria-expanded={expanded}
          onClick={() => setExpanded(value => !value)}
        >
          {expanded ? 'Show less' : 'Read more'}
        </button>
      )}
    </div>
  );
}

function ExpandableTags({ values, limit = 8, empty = 'Not specified' }) {
  const [expanded, setExpanded] = useState(false);
  const items = cleanList(values);

  if (!items.length) {
    return <p className="spd-muted">{empty}</p>;
  }

  return (
    <div className="spd-tags">
      {(expanded ? items : items.slice(0, limit)).map(item => (
        <span className="spd-tag" key={item}>
          {item}
        </span>
      ))}

      {items.length > limit && (
        <button
          type="button"
          className="spd-tag spd-tag-button"
          aria-expanded={expanded}
          onClick={() => setExpanded(value => !value)}
        >
          {expanded ? 'Show less' : `+${items.length - limit} more`}
        </button>
      )}
    </div>
  );
}

export default function PublicServiceProviderProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const [provider, setProvider] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notFound, setNotFound] = useState(false);
  const [reload, setReload] = useState(0);
  const [imageFailed, setImageFailed] = useState(false);
  const [shareMessage, setShareMessage] = useState('');

  useEffect(() => {
    const controller = new AbortController();

    const loadProfile = async () => {
      setLoading(true);
      setError('');
      setNotFound(false);
      setProvider(null);
      setImageFailed(false);
      setShareMessage('');

      try {
        const response = await fetch(
          `${API_BASE}/users/public-vendors/${encodeURIComponent(id || '')}`,
          { signal: controller.signal }
        );

        const data = await response.json().catch(() => null);

        if (!response.ok || !data?.success || !data.vendor) {
          if (!controller.signal.aborted) {
            setNotFound(response.status === 404 || response.status === 400);
          }

          throw new Error(
            data?.message || 'Unable to load this company profile.'
          );
        }

        if (!controller.signal.aborted) {
          setProvider(data.vendor);
        }
      } catch (err) {
        if (!controller.signal.aborted) {
          setError(err.message);
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    loadProfile();

    return () => controller.abort();
  }, [id, reload]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  useEffect(() => {
    if (!provider) return undefined;

    const previousTitle = document.title;
    document.title = `${getCompanyName(provider)} | SeaZex`;

    return () => {
      document.title = previousTitle;
    };
  }, [provider]);

  const copyProfile = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setShareMessage('Profile link copied.');
    } catch {
      setShareMessage(
        'You can copy this profile URL from your browser address bar.'
      );
    }
  };

  const sendMessage = () => {
    if (!provider) return;

    const target = `/messages/${provider._id}`;

    if (user) {
      navigate(target, {
        state: { userName: getCompanyName(provider) }
      });
    } else {
      navigate('/login', {
        state: { from: target }
      });
    }
  };

  const name = provider ? getCompanyName(provider) : '';
  const website = getWebsite(provider?.website);
  const avatar = provider ? resolveAvatar(provider) : '';
  const services = cleanList(provider?.serviceCategories);
  const ports = cleanList(provider?.ports);
  const countries = cleanList(provider?.countries);
  const certifications = cleanList(provider?.certifications);
  const totalRatings = numberValue(provider?.totalRatings);

  const rating =
    totalRatings > 0 && Number.isFinite(Number(provider?.rating))
      ? `${Math.min(5, Math.max(0, Number(provider.rating))).toFixed(1)} / 5`
      : 'No reviews yet';

  const ownProfile =
    user &&
    provider &&
    String(user._id || user.id) === String(provider._id);

  const initials = name
    .split(/\s+/)
    .slice(0, 2)
    .map(word => word.charAt(0))
    .join('')
    .toUpperCase();

  return (
    <div className="spd-page">
      <FrontHeader />

      <main className="spd-shell spd-profile-shell">
        <div className="spd-profile-navigation">
          <Link className="spd-text-button" to="/service-providers">
            <span aria-hidden="true">←</span>
            All service providers
          </Link>

          {provider && (
            <button
              type="button"
              className="spd-button"
              onClick={copyProfile}
            >
              <i className="fas fa-share-alt" aria-hidden="true" />
              Share profile
            </button>
          )}
        </div>

        {shareMessage && (
          <p className="spd-share-message" role="status">
            {shareMessage}
          </p>
        )}

        {loading ? (
          <div className="spd-panel spd-state" role="status">
            <i className="fas fa-spinner fa-spin" aria-hidden="true" />
            <h2>Loading company profile</h2>
            <p>Getting the latest company information…</p>
          </div>
        ) : error ? (
          <div className="spd-panel spd-state" role="alert">
            <i className="fas fa-exclamation-circle" aria-hidden="true" />
            <h1>
              {notFound ? 'Profile unavailable' : 'Unable to load profile'}
            </h1>
            <p>{error}</p>

            {notFound ? (
              <Link className="spd-button" to="/service-providers">
                Browse service providers
              </Link>
            ) : (
              <button
                type="button"
                className="spd-button"
                onClick={() => setReload(value => value + 1)}
              >
                Try again
              </button>
            )}
          </div>
        ) : provider ? (
          <>
            <header className="spd-panel spd-profile-header">
              <div className="spd-profile-identity">
                <div className="spd-avatar spd-avatar-large">
                  {avatar && !imageFailed ? (
                    <img
                      src={avatar}
                      alt={`${name} logo`}
                      referrerPolicy="no-referrer"
                      onError={() => setImageFailed(true)}
                    />
                  ) : (
                    <span aria-hidden="true">{initials}</span>
                  )}
                </div>

                <div className="spd-identity">
                  <span className="spd-eyebrow">
                    MARITIME SERVICE PROVIDER
                  </span>

                  <h1>{name}</h1>

                  <div className="spd-meta">
                    {provider.verificationStatus === 'approved' ? (
                      <span className="spd-badge">
                        <i
                          className="fas fa-check-circle"
                          aria-hidden="true"
                        />
                        Verified provider
                      </span>
                    ) : (
                      <span className="spd-muted">
                        Business verification pending
                      </span>
                    )}

                    <span>
                      <i
                        className="fas fa-map-marker-alt"
                        aria-hidden="true"
                      />
                      {provider.location || 'Location not provided'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="spd-profile-stats">
                <div>
                  <strong>{rating}</strong>
                  <span>
                    {totalRatings > 0
                      ? `${totalRatings} ratings`
                      : 'Customer ratings'}
                  </span>
                </div>

                <div>
                  <strong>{numberValue(provider.completedJobs)}</strong>
                  <span>Completed jobs</span>
                </div>

                <div>
                  <strong>{ports.length}</strong>
                  <span>Ports served</span>
                </div>

                <div>
                  <strong>{countries.length}</strong>
                  <span>Countries covered</span>
                </div>
              </div>
            </header>

            <div className="spd-profile-layout">
              <div className="spd-profile-content">
                <section className="spd-panel spd-detail">
                  <h2>About the company</h2>
                  <ExpandableDescription
                    key={provider._id}
                    value={getDescription(provider)}
                  />
                </section>

                <section className="spd-panel spd-detail">
                  <div className="spd-section-heading">
                    <h2>Services & expertise</h2>
                    <span className="spd-count">{services.length}</span>
                  </div>

                  <ExpandableTags
                    key={`services-${provider._id}`}
                    values={services}
                    empty="This company has not listed its services yet."
                  />
                </section>

                <section className="spd-panel spd-detail">
                  <h2>Service coverage</h2>

                  <div className="spd-coverage-grid">
                    <div>
                      <h3>Countries</h3>
                      <ExpandableTags
                        key={`countries-${provider._id}`}
                        values={countries}
                        limit={6}
                        empty="Countries not listed."
                      />
                    </div>

                    <div>
                      <h3>Ports</h3>
                      <ExpandableTags
                        key={`ports-${provider._id}`}
                        values={ports}
                        limit={8}
                        empty="Ports not listed."
                      />
                    </div>
                  </div>
                </section>

                <section className="spd-panel spd-detail">
                  <div className="spd-section-heading">
                    <h2>Certifications</h2>
                    <span className="spd-count">
                      {certifications.length}
                    </span>
                  </div>

                  <ExpandableTags
                    key={`certifications-${provider._id}`}
                    values={certifications}
                    limit={6}
                    empty="No certifications listed."
                  />

                  {certifications.length > 0 && (
                    <p className="spd-small">
                      Certifications listed by the company.
                    </p>
                  )}
                </section>
              </div>

              <aside className="spd-profile-sidebar">
                <section className="spd-panel spd-detail spd-contact">
                  <h2>Connect with this company</h2>

                  <p className="spd-muted">
                    Discuss your vessel requirements, service availability
                    and project scope.
                  </p>

                  {ownProfile ? (
                    <Link
                      className="spd-button spd-button-primary"
                      to="/service-provider/company-profile"
                    >
                      Edit company profile
                    </Link>
                  ) : (
                    <button
                      type="button"
                      className="spd-button spd-button-primary"
                      onClick={sendMessage}
                      disabled={authLoading}
                    >
                      <i className="far fa-envelope" aria-hidden="true" />
                      {user ? 'Send message' : 'Sign in to message'}
                    </button>
                  )}

                  {website && (
                    <a
                      className="spd-button"
                      href={website}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <i className="fas fa-globe" aria-hidden="true" />
                      Visit company website
                    </a>
                  )}

                  <dl className="spd-facts">
                    <div>
                      <dt>Company</dt>
                      <dd>{name}</dd>
                    </div>

                    <div>
                      <dt>Location</dt>
                      <dd>{provider.location || 'Not provided'}</dd>
                    </div>

                    <div>
                      <dt>Years in business</dt>
                      <dd>
                        {cleanText(provider.yearsInBusiness) || 'Not provided'}
                      </dd>
                    </div>

                    <div>
                      <dt>Member since</dt>
                      <dd>{getMemberSince(provider.createdAt)}</dd>
                    </div>
                  </dl>
                </section>
              </aside>
            </div>
          </>
        ) : null}
      </main>

      <FrontFooter />
    </div>
  );
}